import React, { useEffect, useCallback, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { contactlessPaymentActions } from "./services/actions"
import { ContactlessPaymentState } from "./services/reducer"
import styles from "./ContactlessPayment.module.scss"
import Loader from "components/Loaders/Loader/Loader"
import { Button } from "components/Button"
import { languages } from "locales/languages"
import { cdn } from "core/cdn"


interface ContactlessPaymentProps {
  onSuccess?: () => void
  onSwitchToCard?: () => void
}

const ContactlessPayment: React.FC<ContactlessPaymentProps> = ({
  onSuccess,
  onSwitchToCard
}) => {
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)

  const { status } = useSelector(
    (state: { contactlessPayment: ContactlessPaymentState }) => state.contactlessPayment
  )

  const statusRef = useRef(status)
  const onSuccessRef = useRef(onSuccess)
  onSuccessRef.current = onSuccess

  useEffect(() => {
    statusRef.current = status
  }, [status])

  useEffect(() => {
    return () => {
      if (statusRef.current !== "polling" && statusRef.current !== "creating") {
        dispatch(contactlessPaymentActions.reset())
      }
    }
  }, [dispatch])

  useEffect(() => {
    if (status === "success") {
      onSuccessRef.current?.()
    }
  }, [status])

  useEffect(() => {
    if (status === "polling" || status === "failed" || status === "timeout" || status === "cancelled") {
      setLoading(false)
    }
  }, [status])

  const handleRetry = useCallback(() => {
    dispatch(contactlessPaymentActions.reset())
  }, [dispatch])

  const titleByStatus = {
    "cancelled": languages.paymentCancelled,
    "timeout": languages.paymentTimeout,
    "failed": languages.paymentFailed,
  }
  const iconByStatus = {
    "cancelled": cdn("/icons/payment/payment_failed.svg"),
    "timeout": cdn("/icons/payment/payment_expired.svg"),
    "failed": cdn("/icons/payment/payment_failed.svg"),
  }

  const renderContent = () => {
    switch (status) {
      case "creating":
        return (
          <div className={styles.waiting}>
            <Loader />
            <p className={styles.message}>
              {languages.initializingPayment}
            </p>
          </div>
        )

      case "waitingStaff":
        return (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}>
            <div className={styles.polling} style={{ marginBottom: '30px' }}>
              <img src={cdn("/images/tlc_terminee_illustration.svg")} alt="pharmacist" style={{ width: '160px', height: '160px' }} />
              {languages.waitingStaff}
              <p style={{ fontSize: '32px', fontWeight: 500 }}>
                {languages.waitingStaffSubtitlePrefix} <b>{languages.waitingStaffSubtitleTerminal}</b>. <b>{languages.waitingStaffSubtitleMandatory}</b>
              </p>
            </div>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              padding: '27px 100px',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '40px'
            }}>
              <Button
                type="primary"
                loading={loading}
                disabled={loading}
                onClick={() => {
                  setLoading(true)
                  dispatch(contactlessPaymentActions.waitingStaffSuccess())
                }}
              >
                {languages.launchContactlessPayment}
              </Button>
            </div>
          </div>
        )

      case "polling":
        return (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}>
            <div className={styles.polling}>
              <img src={cdn("/icons/payment/contactless_payment.svg")} alt="contactless payment" style={{ width: '160px', height: '160px' }} />
              {languages.waitingContactlessPaymentInfo}
            </div>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              padding: '27px 100px',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '40px'
            }}>
              <Button type="primary" loading={true} disabled={true}>
                {languages.waitingContactlessPaymentButton}
              </Button>
            </div>
          </div>
        )

      case "success":
        return (
          <div className={styles.success}>
            <img src={cdn("/icons/checked_circle-green.svg")} alt="success" />
            <p className={styles.message}>{languages.paymentAccepted}</p>
          </div>
        )

      case "cancelled":
      case "timeout":
      case "failed":
        return (
          <div>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}>
              <img
                style={{
                  marginBottom: '40px',
                }}
                src={iconByStatus[status]}
                alt={`payment ${status}`}
              />
              <p style={{
                color: '#211452',
                textAlign: 'center',
                fontFamily: 'Now',
                fontSize: '48px',
                fontWeight: '500',
                lineHeight: 'normal',
              }}>{titleByStatus[status]}</p>
              <p style={{
                color: '#211452',
                textAlign: 'center',
                fontFamily: 'Now',
                fontSize: '34px',
                fontWeight: '500',
                lineHeight: 'normal',
              }}>{languages.useAnotherPaymentMethodInfo}</p>
            </div>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '40px',
              marginTop: '30px',
            }}>
              <Button onClick={handleRetry} type="primary" style={{
                display: 'flex',
                padding: '27px 30px',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '10px',
                width: '500px',
                height: '100px',
              }}>
                {languages.retry}
              </Button>
              <Button onClick={onSwitchToCard} style={{
                display: 'flex',
                padding: '27px 30px',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '10px',
                width: '500px',
                height: '100px',
              }}>
                {languages.payByCard}
              </Button>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return <div className={styles.container}>{renderContent()}</div>
}

export default ContactlessPayment

